import React from "react";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import {
  AnimatedCounter,
  GlassCard,
} from "../../FatigueManagement/components/primitives";
import { formatMoney, type RoiResult } from "../data/model";

interface Props {
  result: RoiResult;
  attribution: number;
  onEditAssumptions: () => void;
}

/** Headline attributed ROI plus a full per-category breakdown table. */
export const SavingsSection: React.FC<Props> = ({
  result,
  attribution,
  onEditAssumptions,
}) => {
  const theme = useTheme();

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Projected Annual ROI
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Business-outcome savings, at {Math.round(attribution * 100)}%
            attribution to the programme.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TuneRoundedIcon />}
          onClick={onEditAssumptions}
        >
          Edit assumptions
        </Button>
      </Stack>

      {/* Headline cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        <GlassCard accent={theme.palette.success.main} glow>
          <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1.5 }}>
            Attributed saving / year
          </Typography>
          <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5 }}>
            <AnimatedCounter value={result.totalAttributed} prefix="£" />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            What we credibly claim
          </Typography>
        </GlassCard>

        <GlassCard>
          <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1.5 }}>
            Total opportunity
          </Typography>
          <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5 }}>
            <AnimatedCounter value={result.totalHeadline} prefix="£" />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Full annual cost pool addressed
          </Typography>
        </GlassCard>

        <GlassCard>
          <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1.5 }}>
            Per employee / year
          </Typography>
          <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5 }}>
            <AnimatedCounter value={result.perEmployeeAttributed} prefix="£" />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Attributed saving ÷ headcount
          </Typography>
        </GlassCard>
      </Box>

      {/* Breakdown table */}
      <GlassCard>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          Breakdown by category
        </Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" aria-label="ROI breakdown by category">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Opportunity
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Gross saving
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Attributed
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Basis</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.categories.map((c) => (
                <TableRow key={c.key} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{c.label}</TableCell>
                  <TableCell align="right">{formatMoney(c.headline)}</TableCell>
                  <TableCell align="right">{formatMoney(c.grossSaving)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatMoney(c.attributedSaving)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {c.basis}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow
                sx={{ bgcolor: alpha(theme.palette.success.main, 0.08) }}
              >
                <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>
                  {formatMoney(result.totalHeadline)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>
                  {formatMoney(result.totalGross)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>
                  {formatMoney(result.totalAttributed)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}
        >
          Health metrics (sleep, HRV, recovery) are leading indicators. These
          figures are the business outcomes they drive — the numbers finance
          actually saves against.
        </Typography>
      </GlassCard>
    </Stack>
  );
};
